import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';

/**
 * Full-screen photo viewer of a listing. Opens on the photo the traveller
 * clicked (`startIndex`) and browses ALL the listing's photos in their saved
 * order: arrows, keyboard arrows, horizontal swipe on phones, a counter
 * ("3 / 8"). Escape or the cross closes it; the page behind keeps its scroll
 * position (the dialog only locks it while open) and focus goes back to the
 * clicked photo. Images are shown whole (object-contain), never distorted; a
 * photo that fails to load shows a notice instead. URLs are used as given.
 */
export default function PhotoLightbox({ images, open, startIndex = 0, onOpenChange, alt }) {
  const { t } = useTranslation();
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const count = list.length;
  const [index, setIndex] = useState(startIndex);
  const [failed, setFailed] = useState({});
  const touch = useRef(null);

  useEffect(() => { if (open) setIndex(Math.min(Math.max(startIndex, 0), Math.max(count - 1, 0))); }, [open, startIndex, count]);

  if (!count) return null;
  const current = Math.min(index, count - 1);
  const go = (delta) => setIndex((i) => (Math.min(i, count - 1) + delta + count) % count);
  const onKeyDown = (e) => {
    if (count < 2) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const onTouchStart = (e) => { const p = e.touches[0]; touch.current = { x: p.clientX, y: p.clientY }; };
  const onTouchEnd = (e) => {
    const start = touch.current;
    touch.current = null;
    if (!start || count < 2) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - start.x;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(p.clientY - start.y)) go(dx < 0 ? 1 : -1);
  };
  const navBtn = 'absolute top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/95" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex flex-col outline-none"
          onKeyDown={onKeyDown}
          aria-describedby={undefined}
          data-testid="photo-lightbox"
        >
          <DialogPrimitive.Title className="sr-only">{t('serviceDetail.gallery.title', { name: alt })}</DialogPrimitive.Title>
          <div className="flex items-center justify-between gap-3 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2 text-white">
            <span className="text-sm font-medium tabular-nums bg-black/50 rounded-full px-3 py-1" data-testid="lightbox-counter" aria-live="polite">
              {current + 1} / {count}
            </span>
            <DialogPrimitive.Close
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-primary flex items-center justify-center shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={t('serviceDetail.gallery.close')}
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6" />
            </DialogPrimitive.Close>
          </div>
          <div
            className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16 pb-[max(1rem,env(safe-area-inset-bottom))] select-none"
            style={{ touchAction: 'pan-y' }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="lightbox-stage"
          >
            {failed[current] ? (
              <div className="flex flex-col items-center gap-2 text-white/80" data-testid="lightbox-unavailable">
                <ImageOff className="w-10 h-10" />
                <p className="text-sm">{t('serviceDetail.gallery.unavailable')}</p>
              </div>
            ) : (
              <img
                key={list[current]}
                src={list[current]}
                alt={`${alt} ${current + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg"
                draggable={false}
                onError={() => setFailed((f) => ({ ...f, [current]: true }))}
                data-testid="lightbox-image"
              />
            )}
            {count > 1 && (
              <>
                <button type="button" onClick={() => go(-1)} className={`${navBtn} left-2 sm:left-4`}
                  aria-label={t('scooters.detail.prevPhoto')} data-testid="lightbox-prev">
                  <ChevronLeft className="w-6 h-6 text-primary" />
                </button>
                <button type="button" onClick={() => go(1)} className={`${navBtn} right-2 sm:right-4`}
                  aria-label={t('scooters.detail.nextPhoto')} data-testid="lightbox-next">
                  <ChevronRight className="w-6 h-6 text-primary" />
                </button>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
